import API from "common-ui-components/components/utils/API";
import { useEffect, useMemo, useRef, useState } from "react";

const useTableRecords = (
  url,
  reqMethod,
  directRecords,
  propTotal,
  recordsToExclue,
  uniqueAccessor,
  tableConfig,
  clientSide, // boolean — when true, all filter/search/sort is handled in-memory
  valueLabelMap, // { [column]: { [value]: label } } — for resolving raw values to display labels during search
  refreshKey,
  setTotal,
  setResult,
  setIsRestricted,
  isPayment,
  visitedRecords,
  setVisitedRecords,
  defaultPageSize = 10, // 1. Catch the pageSize here, fallback to 10 if missing
  setApiColumns,
  setApiFilterConfig,
  apiClient = API,
  paginationNeeded = true,
) => {
  const [allRecords, setAllRecords] = useState([]);

  // 2. Initialize the state with our custom defaultPageSize on frame one
  const [pagination, setPagination] = useState({
    page: 1,
    size: defaultPageSize,
    total: 0,
  });

  // 3. Keep tracking the default size if the prop changes dynamically
  useEffect(() => {
    setPagination((prev) => ({ ...prev, size: defaultPageSize }));
  }, [defaultPageSize]);

  // 4. FIX: Stop this effect from overwriting your custom size back to 10 during search/filter
  useEffect(() => {
    if (clientSide) return;
    setPagination((prev) => ({ ...prev, page: 1, size: defaultPageSize }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(tableConfig.search), JSON.stringify(tableConfig.filter)]);

  const records = useMemo(() => {
    if (!clientSide) return allRecords;

    let result = allRecords;

    // 1. Filter — supports both single-value and multi-value (array) filter types
    if (tableConfig.filter.length > 0) {
      result = result.filter((record) =>
        tableConfig.filter.every((f) => {
          const val = record[f.column];
          return Array.isArray(f.filterValue)
            ? f.filterValue.includes(val)
            : val === f.filterValue;
        }),
      );
    }

    // 2. Search — resolve raw record value to its display label (if a mapping
    //    exists for that column), then do a case-insensitive substring match.
    //    e.g. record value "QUICK_SUPPORT" → label "Quick Connect" → matches "quick"
    if (tableConfig.search.length > 0) {
      result = result.filter((record) =>
        tableConfig.search.every((s) => {
          const rawVal = record[s.column];
          // Resolve to display label when a mapping exists for this column,
          // otherwise fall back to the raw value
          const displayVal = valueLabelMap[s.column]?.[rawVal] ?? rawVal;
          if (displayVal == null) return false;
          return String(displayVal)
            .toLowerCase()
            .includes(s.searchKey.toLowerCase());
        }),
      );
    }

    // 3. Sort — nulls always sink to the bottom regardless of order
    if (tableConfig.sort?.column) {
      const { column, order } = tableConfig.sort;
      result = [...result].sort((a, b) => {
        const aVal = a[column];
        const bVal = b[column];
        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        const comparison =
          typeof aVal === "string" ? aVal.localeCompare(bVal) : aVal - bVal;
        return order === "asc" ? comparison : -comparison;
      });
    }

    return result;
  }, [
    allRecords,
    clientSide,
    tableConfig.filter,
    tableConfig.search,
    tableConfig.sort,
    valueLabelMap,
  ]);

  const controllerRef = useRef(null);

  useEffect(() => {
    const getRecords = async () => {
      if (directRecords) {
        // Use propTotal if provided, otherwise fall back to directRecords.length
        const totalCount =
          propTotal !== undefined ? propTotal : directRecords.length;
        setPagination((prev) => ({ ...prev, total: totalCount }));
        setTotal(totalCount);

        if (!paginationNeeded) {
          setAllRecords(directRecords);
          return;
        }

        const startingIndex = (pagination.page - 1) * pagination.size;
        const endingIndex = startingIndex + pagination.size;
        setAllRecords(directRecords.filter(
          (_record, index) => index + 1 > startingIndex && index + 1 <= endingIndex,
        ));
        return;
      }

      if (url == null || url.length === 0) return;

      const serverTableConfig = clientSide
        ? { ...tableConfig, filter: [], search: [], sort: {} }
        : tableConfig;

      const [actualURL, searchParams] = url.split("?");
      controllerRef.current?.abort();
      controllerRef.current = new AbortController();
      const params = new URLSearchParams(searchParams);
      params.append("page", pagination.page - 1);
      params.append("size", pagination.size);
      setTotal(null);

      try {
        let response = null;
        if (reqMethod && reqMethod === "GET") {
          response = await apiClient.get(
            `${actualURL}?${params}`,
            serverTableConfig,
            {
              signal: controllerRef.current.signal,
            },
          );
        } else {
          response = await apiClient.post(
            `${actualURL}?${params}`,
            serverTableConfig,
            {
              signal: controllerRef.current.signal,
            },
          );
        }

        if (response.status.code === 412) {
          setIsRestricted(true);
          setPagination((prev) => ({ ...prev, total: 0 }));
          setTotal(-2);
          return;
        }
        if (response.status.code === 402) {
          isPayment(true);
          setPagination((prev) => ({ ...prev, total: 0 }));
          setTotal(-2);
          return;
        }
        if (response && response.status.success) {
          const { total } = response.body;
          const totalPages = Math.ceil(total / pagination.size);

          if (total > 0 && pagination.page > totalPages) {
            setPagination((prev) => ({ ...prev, page: totalPages }));
            return;
          }

          setPagination((prev) => ({ ...prev, total }));
          setTotal(total);

          if (response.body.tableMeta) {
            const newColumns = response.body.tableMeta.columns || [];
            setApiColumns(newColumns);
            const newFilterConfig =
              response.body.tableMeta.filterConfig || null;
            setApiFilterConfig(newFilterConfig);
          }

          if (setResult) setResult(response.body.result);

          let rawRecords = response.body.result;

          rawRecords = rawRecords.map((record) => {
            if (record.customFields) {
              const { customFields, ...rest } = record;
              return {
                ...rest,
                ...customFields,
              };
            }
            return record;
          });

          if (recordsToExclue?.length > 0 && uniqueAccessor) {
            const idsToExclude = recordsToExclue.map(
              (item) => item[uniqueAccessor],
            );
            rawRecords = rawRecords.filter(
              (item) => !idsToExclude.includes(item[uniqueAccessor]),
            );
          }
          setAllRecords(rawRecords);
        }
      } catch (error) {
        if (error.name === "AbortError" || error.name === "CanceledError")
          return;
        // console.log("Request aborted");
        console.error(`Error fetching records for ${url}`, error);
      }
    };

    getRecords();
  }, [
    pagination.page,
    pagination.size,
    clientSide ? null : JSON.stringify(tableConfig.search),
    clientSide ? null : JSON.stringify(tableConfig.filter),
    clientSide ? null : JSON.stringify(tableConfig.sort),
    url,
    refreshKey,
    directRecords,
    paginationNeeded,
  ]);

  return { records, pagination, setPagination };
};

export default useTableRecords;
